import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Settings, Edit, CheckCircle, XCircle, Clock, Rocket, Loader2, Image as ImageIcon, Sparkles, Undo2, Redo2, Save, Check, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import GenerationNotificationModal from '../components/GenerationNotificationModal';
import LoadingAnimation from '../components/LoadingAnimation';

interface CalendarDay {
  date: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  events: {
    confirmed: boolean;
    posted: boolean;
    count: number;
  };
}

interface ScheduledPost {
  id: number;
  scheduled_at: string;
  post_description?: string | null;
  confirmed: boolean;
  posted_at: string | null;
  file_name?: string | null;
  imageUrl?: string;
  thumbnailUrl?: string;
}

interface ConfigData {
  media_autopilot_on?: boolean;
  media_agent_on?: boolean;
  last_auto_gen_date?: string | null;
}

export const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [weekViewActive, setWeekViewActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState<number>(0);
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingPost, setUpdatingPost] = useState(false);
  // Add state for editing post
  const [editingPost, setEditingPost] = useState<ScheduledPost | null>(null);
  const [editedScheduledTime, setEditedScheduledTime] = useState<string>('');
  const [editedScheduledDate, setEditedScheduledDate] = useState<string>('');
  // Configuration panel state
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [configData, setConfigData] = useState<ConfigData>({
    media_autopilot_on: false,
    media_agent_on: true,
    last_auto_gen_date: null
  });
  const [updatingConfig, setUpdatingConfig] = useState(false);
  // Chat editing mode
  const [editingImageChat, setEditingImageChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChatMessage, setSendingChatMessage] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState<Record<number, boolean>>({});
  // Separate state for tracking background image refreshes that shouldn't show loading UI
  const [backgroundRefreshing, setBackgroundRefreshing] = useState<Record<number, boolean>>({});
  const [imageEditSuccessMessage, setImageEditSuccessMessage] = useState<string>(''); 
  const [imageHasBeenEdited, setImageHasBeenEdited] = useState<boolean>(false);
  // Editing description states
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  
  // EditarImagen-style edit states for Modificar
  const [instruction, setInstruction] = useState<string>('');
  const [history, setHistory] = useState<{ id: number; url: string }[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showEditSuccess, setShowEditSuccess] = useState<boolean>(false);
  const hasEdits = currentIndex > 0;
  
  // Initial loading message state
  const [showInitialLoadingMessage, setShowInitialLoadingMessage] = useState(false);
  // Show generation modal overlay triggered from PilotoAutomatico via localStorage
  const [showCalendarGenerationModal, setShowCalendarGenerationModal] = useState(false);
  
  // Single dialog system
  type DialogType = 'none' | 'generarNueva';
  const [activeDialog, setActiveDialog] = useState<DialogType>('none');
  
  // Loading overlay for generating a completely new image (match Crear flow)
  const [generatingNewImage, setGeneratingNewImage] = useState<boolean>(false);
  const GENERATE_DURATION_MS = 70000; // same as modo "rapido" (70s)
  
  // Auto-dismiss success messages but keep track of edit state
  useEffect(() => {
    if (imageEditSuccessMessage) {
      // Set the flag that an image has been edited
      setImageHasBeenEdited(true);
      
      // Still clear the success message after some time
      const timer = setTimeout(() => {
        setImageEditSuccessMessage('');
        // But don't reset imageHasBeenEdited here
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, [imageEditSuccessMessage]);
  
  // Reset image edit tracking when closing edit mode
  useEffect(() => {
    if (!editingPost) {
      setImageHasBeenEdited(false);
    }
  }, [editingPost]);

  // Auto-hide the success chip for EditarImagen-style edits
  useEffect(() => {
    if (!showEditSuccess) return;
    const t = window.setTimeout(() => setShowEditSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [showEditSuccess]);

  // Count posts (only future dates including today)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to beginning of today
  
  // Count confirmed posts
  const confirmedPosts = scheduledPosts.filter(post => {
    const postDate = new Date(post.scheduled_at);
    return post.confirmed && postDate >= today; // Only count confirmed posts scheduled for today or future
  }).length;
  
  // Count unconfirmed posts
  const unconfirmedPosts = scheduledPosts.filter(post => {
    const postDate = new Date(post.scheduled_at);
    return !post.confirmed && !post.posted_at && postDate >= today; // Only count unconfirmed posts for today or future
  }).length;
  
  // Total of all future posts (both confirmed and unconfirmed)
  const totalFuturePosts = scheduledPosts.filter(post => {
    const postDate = new Date(post.scheduled_at);
    return postDate >= today; // All posts scheduled for today or future
  }).length;

  const monthNames = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  const dayNames = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Initialize EditarImagen-style editing session when entering Modificar
  useEffect(() => {
    const init = async () => {
      if (editingImageChat && editingPost) {
        try {
          setImageLoading(true);
          const { data: auth } = await supabase.auth.getUser();
          const user = auth?.user;
          if (!user) return;
          const { data: profile } = await supabase
            .from('profiles')
            .select('company_id')
            .eq('id', user.id)
            .single();
          const compId = profile?.company_id;
          if (!compId) return;
          const { data: imgData, error: imgErr } = await supabase
            .schema(compId)
            .from('imagenes_generadas')
            .select('file_name')
            .eq('id', editingPost.id)
            .single();
          if (imgErr || !imgData?.file_name) return;
          const filePath = `generadas/full/${imgData.file_name}`;
          const { data: signed } = await supabase.storage
            .from(compId)
            .createSignedUrl(filePath, 3600);
          if (signed?.signedUrl) {
            setHistory([{ id: editingPost.id, url: signed.signedUrl }]);
            setCurrentIndex(0);
          }
        } catch (e) {
          console.error('Error inicializando edición:', e);
        } finally {
          setImageLoading(false);
        }
      } else {
        // Reset session when exiting
        setHistory([]);
        setCurrentIndex(-1);
        setInstruction('');
        setShowEditSuccess(false);
      }
    };
    init();
  }, [editingImageChat, editingPost]);

  // Function to get the week number of a date within the current month view
  const getWeekNumber = (day: CalendarDay): number => {
    const allDays = getDaysInMonth(currentDate);
    const dayIndex = allDays.findIndex(d => 
      d.date === day.date && d.isCurrentMonth === day.isCurrentMonth
    );
    return Math.floor(dayIndex / 7);
  };

  // EditarImagen-style: send instruction to edit current image
  const handleSendInstruction = async () => {
    try {
      const trimmed = instruction.trim();
      if (!trimmed || !editingPost) return;

      setIsSubmitting(true);
      setError(null);
      setShowEditSuccess(false);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setError('No se pudo identificar al usuario.');
        setIsSubmitting(false);
        return;
      }

      const currentImageId = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].id : editingPost.id;

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/ad59e61b-6086-4527-8d7f-22692e3a5329', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, imagen_id: currentImageId, instruction: trimmed })
      });

      if (!response.ok) {
        throw new Error('Error al enviar la instrucción');
      }

      const data = await response.json();
      const newImageId: number | undefined = data?.edited_image_id ?? data?.imagen_id ?? data?.id;
      if (!newImageId) {
        throw new Error('No se recibió el ID de la imagen editada');
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();
      const compId = profile?.company_id;
      if (!compId) throw new Error('No se encontró la empresa del usuario');

      const { data: newImageData, error: newImgErr } = await supabase
        .schema(compId)
        .from('imagenes_generadas')
        .select('file_name')
        .eq('id', newImageId)
        .single();
      if (newImgErr || !newImageData?.file_name) {
        throw new Error('No se pudo cargar la nueva imagen editada');
      }

      const newFilePath = `generadas/full/${newImageData.file_name}`;
      const { data: newSigned } = await supabase.storage.from(compId).createSignedUrl(newFilePath, 3600);
      if (!newSigned?.signedUrl) {
        throw new Error('No se pudo obtener la URL firmada de la imagen editada');
      }

      setHistory((prev) => {
        const base = currentIndex >= 0 ? prev.slice(0, currentIndex + 1) : prev;
        return [...base, { id: newImageId, url: newSigned.signedUrl }];
      });
      setCurrentIndex((prev) => (prev >= 0 ? prev + 1 : 0));

      setInstruction('');
      setShowEditSuccess(true);
    } catch (e: any) {
      console.error('Error enviando instrucción de edición:', e);
      setError(e?.message || 'Ocurrió un error al editar la imagen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUndo = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRedo = () => {
    if (currentIndex >= 0 && currentIndex < history.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const persistActiveFlags = async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();
      const compId = profile?.company_id;
      if (!compId) return;

      const ids = Array.from(new Set(history.map((h) => h.id)));
      const activeId = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].id : editingPost?.id;
      if (ids.length === 0 || !activeId) return;

      await supabase
        .schema(compId)
        .from('imagenes_generadas')
        .update({ active: false })
        .in('id', ids);

      await supabase
        .schema(compId)
        .from('imagenes_generadas')
        .update({ active: true })
        .eq('id', activeId);
    } catch (e) {
      console.error('Error guardando estados activos:', e);
    }
  };

  const handleSaveAndExit = async () => {
    try {
      setIsSaving(true);
      setImageLoading(true);
      await persistActiveFlags();
      await fetchScheduledPosts();
    } finally {
      setIsSaving(false);
      setImageLoading(false);
      setEditingImageChat(false);
      setEditingPost(null);
    }
  };

  useEffect(() => {
    // Fetch config data first to determine if loading message should be shown
    fetchConfigData().then(() => {
      fetchScheduledPosts();
    });
    
    const refreshInterval = setInterval(() => {
      // Pass true to indicate this is a background refresh
      fetchScheduledPosts(true);
    }, 20000);
    
    // Clean up the interval when component unmounts
    return () => clearInterval(refreshInterval);
  }, []);

  // On mount, check if we should show the generation message modal (set by PilotoAutomatico)
  useEffect(() => {
    try {
      const flag = window.localStorage.getItem('calendar_show_generation_message');
      if (flag === 'true') {
        setShowCalendarGenerationModal(true);
        window.localStorage.removeItem('calendar_show_generation_message');
      }
    } catch {}
  }, []);
  
  // We no longer reset week view when month changes to allow navigation between months in weekly view
  // This allows users to go to the next/previous month while staying in weekly view
  useEffect(() => {
    // Only reset currentWeekStart when month changes to ensure we start at the first week
    // but keep weekViewActive true to maintain the weekly view
    if (weekViewActive) {
      setCurrentWeekStart(0);
    }
  }, [currentDate.getMonth(), currentDate.getFullYear()]);

  // Set up initial values when editing a post
  useEffect(() => {
    if (editingPost) {
      const scheduledDate = new Date(editingPost.scheduled_at);
      setEditedScheduledDate(scheduledDate.toISOString().split('T')[0]);
      setEditedScheduledTime(scheduledDate.toTimeString().substring(0, 5));
      // Initialize the description editing field
      setEditedDescription(editingPost.post_description || '');
    }
  }, [editingPost]);

  // Ensure editedDescription is always in sync with the latest backend data after scheduledPosts are refreshed
  useEffect(() => {
    if (editingDescription && editingPost) {
      const updatedPost = scheduledPosts.find(p => p.id === editingPost.id);
      if (updatedPost && updatedPost.post_description !== undefined) {
        setEditedDescription(updatedPost.post_description || '');
      }
    }
  }, [scheduledPosts, editingPost, editingDescription]);

  // Load image URLs for posts that have file_name
  const loadImageUrls = async (posts: ScheduledPost[], companyId: string, isBackgroundRefresh = false) => {
    const loadingState: Record<number, boolean> = {};
    
    // Create loading states for each post
    posts.forEach(post => {
      if (post.file_name) {
        loadingState[post.id] = true;
      }
    });
    
    // Only show loading UI indicators for non-background refreshes
    if (!isBackgroundRefresh) {
      setLoadingImages(loadingState);
    } else {
      // For background refreshes, use the separate tracking state
      setBackgroundRefreshing(loadingState);
    }
    
    // Process each post to get image URLs
    await Promise.all(posts.map(async (post) => {
      if (!post.file_name) return post;
      try {
        const optFilePath = `generadas/opt/${post.file_name}`;
        const fullFilePath = `generadas/full/${post.file_name}`;
        let imageUrl = '';
        let thumbnailUrl = '';

        // Try to get the optimized version for thumbnail
        const { data: optData, error: optError } = await supabase
          .storage
          .from(companyId)
          .createSignedUrl(optFilePath, 3600);

        if (!optError && optData?.signedUrl) {
          thumbnailUrl = optData.signedUrl;
          imageUrl = optData.signedUrl; // Use thumbnail as fallback

          // Now get the full-size image
          const { data: fullData, error: fullError } = await supabase
            .storage
            .from(companyId)
            .createSignedUrl(fullFilePath, 3600);

          if (!fullError && fullData?.signedUrl) {
            imageUrl = fullData.signedUrl;
          }
        } else {
          // Optimized version not found, try using the full version for both
          const { data: fullData, error: fullError } = await supabase
            .storage
            .from(companyId)
            .createSignedUrl(fullFilePath, 3600);

          if (!fullError && fullData?.signedUrl) {
            imageUrl = fullData.signedUrl;
            // Try to generate a thumbnail using transform
            const { data: thumbnailData } = await supabase
              .storage
              .from(companyId)
              .createSignedUrl(fullFilePath, 3600, {
                transform: {
                  width: 300,
                  height: 300,
                  resize: 'cover',
                },
              });
            thumbnailUrl = thumbnailData?.signedUrl || fullData.signedUrl;
          } else {
            // Neither optimized nor full version found
            imageUrl = '';
            thumbnailUrl = '';
          }
        }
        post.imageUrl = imageUrl;
        post.thumbnailUrl = thumbnailUrl;
      } catch (err) {
        console.error('Error loading image for post:', post.id, err);
        post.imageUrl = '';
        post.thumbnailUrl = '';
      } finally {
        // Clear the appropriate loading state based on whether this is a background refresh
        if (isBackgroundRefresh) {
          setBackgroundRefreshing(prev => ({
            ...prev,
            [post.id]: false
          }));
        } else {
          setLoadingImages(prev => ({
            ...prev,
            [post.id]: false
          }));
        }
      }
      return post;
    }));
  };

  const fetchScheduledPosts = async (isBackgroundRefresh = false) => {
    try {
      // Only show loading state on initial load, not during background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { data, error } = await supabase
          .schema(profile.company_id)
          .from('imagenes_generadas')
          .select('id, scheduled_at, confirmed, posted_at, post_description, file_name, active')
          .eq('active', true)
          .not('scheduled_at', 'is', null);

      if (error) throw error;

      // Process the data and fetch image URLs
      const postsWithImages = data || [];
      
      // Load image URLs for posts that have file_name, but only for new posts
      // or posts that don't have URLs yet during background refresh
      if (isBackgroundRefresh) {
        // For background refresh, only load images for new posts or posts without images
        const currentPostIds = new Set(scheduledPosts.map(post => post.id));
        const newPosts = postsWithImages.filter(post => 
          !currentPostIds.has(post.id) || 
          (post.file_name && !scheduledPosts.find(p => p.id === post.id)?.imageUrl)
        );
        
        if (newPosts.length > 0) {
          await loadImageUrls(newPosts, profile.company_id, true);
          
          // Merge new posts with existing ones
          const updatedPosts = [...scheduledPosts];
          
          newPosts.forEach(newPost => {
            const existingIndex = updatedPosts.findIndex(p => p.id === newPost.id);
            if (existingIndex >= 0) {
              updatedPosts[existingIndex] = newPost;
            } else {
              updatedPosts.push(newPost);
            }
          });
          
          setScheduledPosts(updatedPosts);
        }
      } else {
        // For initial load, load all images (not a background refresh)
        await loadImageUrls(postsWithImages, profile.company_id, false);
        setScheduledPosts(postsWithImages);
      }
    } catch (err) {
      console.error('Error fetching scheduled posts:', err);
      if (!isBackgroundRefresh) {
        setError(err instanceof Error ? err.message : 'Error loading scheduled posts');
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  };

  const fetchConfigData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { data, error } = await supabase
        .schema(profile.company_id)
        .from('config')
        .select('media_autopilot_on, media_agent_on, last_auto_gen_date')
        .eq('id', 0)
        .single();

      if (error) {
        console.error('Error fetching config data:', error);
        return;
      }

      const updatedConfigData = {
        media_autopilot_on: data?.media_autopilot_on || false,
        media_agent_on: data?.media_agent_on !== false, // Default to true if not explicitly false
        last_auto_gen_date: data?.last_auto_gen_date
      };
      
      setConfigData(updatedConfigData);
      
      // After setting config data, check if we need to show the initial loading message
      // Show the message if last_auto_gen_date is null (initial generation)
      setShowInitialLoadingMessage(updatedConfigData.last_auto_gen_date === null);
      
      // If showing the message, set a timer to hide it after 7 minutes
      if (updatedConfigData.last_auto_gen_date === null) {
        setTimeout(() => {
          setShowInitialLoadingMessage(false);
        }, 420000);
      }
      
    } catch (err) {
      console.error('Error fetching config data:', err);
    }
  
  };

  const updateConfigSetting = async (setting: 'media_autopilot_on' | 'media_agent_on', value: boolean) => {
    try {
      setUpdatingConfig(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { error } = await supabase
        .schema(profile.company_id)
        .from('config')
        .update({ [setting]: value })
        .eq('id', 0);

      if (error) throw error;

      // Update local state
      setConfigData(prev => ({
        ...prev,
        [setting]: value
      }));

      // If agent is turned off, refresh posts to update their display
      if (setting === 'media_agent_on' && !value) {
        await fetchScheduledPosts();
      }
    } catch (err) {
      console.error(`Error updating ${setting}:`, err);
      setError(err instanceof Error ? err.message : `Error updating configuration`);
    } finally {
      setUpdatingConfig(false);
    }
  };

  const handleConfirmPost = async (postId: number, confirmed: boolean) => {
    try {
      setUpdatingPost(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      const { error } = await supabase
        .schema(profile.company_id)
        .from('imagenes_generadas')
        .update({ confirmed })
        .eq('id', postId);
        
      if (error) throw error;
      await fetchScheduledPosts();
    } finally {
      setUpdatingPost(false);
    }
  };

  // Function to handle saving edited post time and description
  const handleSaveEditedPost = async () => {
    if (!editingPost) return;
    
    try {
      setUpdatingPost(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      // Combine date and time into a single ISO string
      const newScheduledAt = new Date(`${editedScheduledDate}T${editedScheduledTime}`).toISOString();
      
      // Include post_description in update if we're in description editing mode
      const updateData = editingDescription 
        ? { post_description: editedDescription } 
        : { scheduled_at: newScheduledAt };
      
      const { error } = await supabase
        .schema(profile.company_id)
        .from('imagenes_generadas')
        .update(updateData)
        .eq('id', editingPost.id);
        
      if (error) throw error;
      
      await fetchScheduledPosts();
      setEditingPost(null);
      setEditingDescription(false);
    } catch (err) {
      console.error('Error updating post:', err);
      setError(err instanceof Error ? err.message : 'Error updating post');
    } finally {
      setUpdatingPost(false);
    }
  };

  // Function to handle sending chat message for image editing
  const handleSendChatMessage = async () => {
    if (!editingPost || !chatMessage.trim() || sendingChatMessage) return;
    
    try {
      setSendingChatMessage(true);
      setImageLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      // Send the message to the webhook
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/f1691ebe-3fab-40ab-af69-131b93fcde43', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          post_id: editingPost.id,
          message: chatMessage,
          mode: "editar" // Adding the mode parameter with 'editar' value
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.response !== 'success') {
        throw new Error(data.message || 'Failed to process image edit request');
      }
      
      // Set success message and clear the chat message
      setImageEditSuccessMessage('¡Imagen editada con éxito!');
      setChatMessage('');
      
      // Get the user's company ID for updating images
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('No company associated with user');
      }
      
      // Add a delay to ensure the backend has fully processed the image
      // waiting for backend processing...
      await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second delay
      
      await fetchScheduledPosts();
      setImageEditSuccessMessage('¡Imagen editada y actualizada!');
      
    } catch (err) {
      console.error('Error sending chat message:', err);
      setError(err instanceof Error ? err.message : 'Error processing image edit request');
    } finally {
      setSendingChatMessage(false);
      setImageLoading(false);
    }
  };

  // Function to handle generating a completely new image and description
  const handleGenerarNuevaImagen = async () => {
  if (!editingPost) return;

  setImageEditSuccessMessage(''); // Clear previous
  // Generando nueva imagen para post en curso

  try {
    setSendingChatMessage(true);
    setActiveDialog('none'); // Close the confirmation dialog
    setGeneratingNewImage(true); // Show overlay with progress bar matching Crear
    // Auto-hide overlay at 70s to match Crear flow
    window.setTimeout(() => setGeneratingNewImage(false), GENERATE_DURATION_MS);
    // Considerar que habrá cambios: mostrar 'Listo'
    setImageHasBeenEdited(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/f1691ebe-3fab-40ab-af69-131b93fcde43', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: user.id,
        post_id: editingPost.id,
        mode: "generar_nueva"
      }),
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    const data = await response.json();

    if (data.response !== 'success') {
      throw new Error(data.message || 'Failed to process new image generation request');
    }
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (!profile?.company_id) {
      throw new Error('No company associated with user');
    }
    
    // Refrescar posts sin bloquear el cierre del overlay
    fetchScheduledPosts().catch(() => {});
    
  } catch (err) {
    console.error('Error generating new image:', err);
    setError(err instanceof Error ? err.message : 'Error generating new image');
    setGeneratingNewImage(false);
    setImageHasBeenEdited(false);
  } finally {
    setSendingChatMessage(false);
    setImageLoading(false);
    // No forzar cierre aquí; el overlay se cierra por timeout o en catch
  }
};

  const getDaysInMonth = (date: Date): CalendarDay[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const daysInMonth = last.getDate();
    
    // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
    let firstDayOfWeek = firstDay.getDay();
    // Convert Sunday (0) to 6 for our Monday-first calendar
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    const today = new Date();
    const isToday = (date: Date) => 
      date.getDate() === today.getDate() && 
      date.getMonth() === today.getMonth() && 
      date.getFullYear() === today.getFullYear();
    
    const isPast = (date: Date) => date < today;
    
    const result: CalendarDay[] = [];
    
    // Add days from previous month
    const prevMonthLastDate = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dayDate = new Date(year, month - 1, prevMonthLastDate - i);
      result.push({
        date: prevMonthLastDate - i,
        isCurrentMonth: false,
        isToday: false,
        isPast: isPast(dayDate),
        events: { confirmed: false, posted: false, count: 0 }
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i);
      
      // Check for scheduled posts on this date
      const dayEvents = {
        confirmed: false,
        posted: false,
        count: 0
      };
      
      scheduledPosts.forEach(post => {
        if (!post.scheduled_at) return;
        
        const postDate = new Date(post.scheduled_at);
        if (
          postDate.getDate() === i &&
          postDate.getMonth() === month &&
          postDate.getFullYear() === year
        ) {
          dayEvents.count++;
          
          // Check if the post is confirmed
          if (post.confirmed) {
            dayEvents.confirmed = true;
          }
          
          // Check if the post is already posted
          if (post.posted_at) {
            dayEvents.posted = true;
          }
        }
      });
      
      result.push({
        date: i,
        isCurrentMonth: true,
        isToday: isToday(currentDate),
        isPast: isPast(currentDate),
        events: dayEvents
      });
    }
    
    // Add just enough days from next month to complete the last row
    // Calculate how many days we need to add to complete the last row
    const daysInCurrentRow = result.length % 7;
    const daysToAdd = daysInCurrentRow === 0 ? 0 : 7 - daysInCurrentRow;
    
    for (let i = 1; i <= daysToAdd; i++) {
      const dayDate = new Date(year, month + 1, i);
      result.push({
        date: i,
        isCurrentMonth: false,
        isToday: false,
        isPast: isPast(dayDate),
        events: { confirmed: false, posted: false, count: 0 }
      });
    }
    
    return result;
  };

  // Get only the days for the current week in week view
  const getWeekDays = (): CalendarDay[] => {
    const allDays = getDaysInMonth(currentDate);
    
    if (!weekViewActive) return allDays;
    
    // Get the start and end indices for the current week
    const startIndex = currentWeekStart * 7;
    const endIndex = startIndex + 6;
    
    return allDays.slice(startIndex, endIndex + 1);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    // Get all days in the current month view
    const allDays = getDaysInMonth(currentDate);
    // Calculate total weeks based on the actual number of days
    const totalWeeks = Math.ceil(allDays.length / 7);
    
    if (direction === 'prev') {
      if (currentWeekStart > 0) {
        // If not at the first week, just move to previous week
        setCurrentWeekStart(prev => prev - 1);
      } else {
        // If at the first week, go to previous month and set to last week
        handlePrevMonth();
        // Set timeout to ensure currentDate is updated before calculating new totalWeeks
        setTimeout(() => {
          const newDays = getDaysInMonth(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
          const newTotalWeeks = Math.ceil(newDays.length / 7);
          setCurrentWeekStart(newTotalWeeks - 1);
        }, 0);
      }
    } else if (direction === 'next') {
      // Check if we're at the last week of the current month
      const currentWeekDays = allDays.slice(currentWeekStart * 7, (currentWeekStart + 1) * 7);
      // Count days in current week that belong to next month
      const nextMonthDaysInCurrentWeek = currentWeekDays.filter(day => !day.isCurrentMonth && day.date < 15).length;
      
      // If more than half the days in the current week are from next month, go to next month
      if (nextMonthDaysInCurrentWeek >= 4) {
        handleNextMonth();
        setCurrentWeekStart(0);
      } else if (currentWeekStart < totalWeeks - 1) {
        // If not at the last week, just move to next week
        setCurrentWeekStart(prev => prev + 1);
      } else {
        // If at the last week, go to next month and set to first week
        handleNextMonth();
        setCurrentWeekStart(0);
      }
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const handleDateClick = (day: CalendarDay, index: number) => {
    if (day.isCurrentMonth) {
      const newDate = new Date(currentDate);
      newDate.setDate(day.date);
      setSelectedDate(newDate);
      
      // Activate week view and set the current week (mobile only)
      if (!weekViewActive && isMobile) {
        setWeekViewActive(true);
        // Calculate which week this day belongs to
        const weekNumber = Math.floor(index / 7);
        setCurrentWeekStart(weekNumber);
      }
    }
  };

  const handleCloseWeekView = () => {
    if (weekViewActive) {
      setWeekViewActive(false);
    }
  };

  const getPostsForSelectedDate = () => {
    if (!selectedDate) return [];
    
    return scheduledPosts.filter(post => {
      if (!post.scheduled_at) return false;
      
      const postDate = new Date(post.scheduled_at);
      return (
        postDate.getDate() === selectedDate.getDate() &&
        postDate.getMonth() === selectedDate.getMonth() &&
        postDate.getFullYear() === selectedDate.getFullYear()
      );
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Find the next unconfirmed post and select it (only future posts including today)
  const showNextUnconfirmedPost = () => {
    // Set today to beginning of day for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Filter for unconfirmed posts that are scheduled for today or future
    const futureUnconfirmedPosts = scheduledPosts.filter(post => {
      const postDate = new Date(post.scheduled_at);
      return !post.confirmed && !post.posted_at && postDate >= today;
    });
    
    if (futureUnconfirmedPosts.length > 0) {
      // Sort by scheduled date (earliest first)
      futureUnconfirmedPosts.sort((a, b) => 
        new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
      );
      
      const nextPost = futureUnconfirmedPosts[0];
      const postDate = new Date(nextPost.scheduled_at);
      
      // Set the current date to the month containing this post
      setCurrentDate(new Date(postDate.getFullYear(), postDate.getMonth(), 1));
      
      // Set the selected date to the post date
      setSelectedDate(postDate);
      
      // Calculate which week this day belongs to and activate week view on mobile only
      const allDays = getDaysInMonth(new Date(postDate.getFullYear(), postDate.getMonth(), 1));
      const dayIndex = allDays.findIndex(day => 
        day.isCurrentMonth && day.date === postDate.getDate()
      );
      
      if (dayIndex !== -1) {
        const weekNumber = Math.floor(dayIndex / 7);
        setCurrentWeekStart(weekNumber);
        // Only activate week view on mobile devices
        if (isMobile) {
          setWeekViewActive(true);
        }
      }
    }
  };


  const calendarDays = getDaysInMonth(currentDate);
  const displayDays = weekViewActive ? getWeekDays() : calendarDays;
  const selectedDatePosts = getPostsForSelectedDate();

  // Configuration Panel Component
  const ConfigurationPanel = () => {
    return (
      <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm z-50 flex items-center justify-center md:p-4">
        <div className={`bg-white md:rounded-lg shadow-lg max-w-md w-full ${isMobile ? 'absolute bottom-0 rounded-t-xl animate-slide-up' : ''}`}>
          <div className="flex items-center justify-between p-6 border-b">
            {!isMobile && <h3 className="text-lg font-medium text-gray-900">Configuración del Calendario</h3>}
            <button
              onClick={() => setShowConfigPanel(false)}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Piloto Automático Toggle */}
            <div className="flex flex-row items-center justify-between gap-4">
              <div>
                <h4 className="text-base font-medium text-gray-900">Piloto Automático</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Si activas el piloto automático, Atlas Studio generará y subirá contenido de forma automática, sin necesidad de tu confirmación
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={configData.media_autopilot_on}
                onClick={() => updateConfigSetting('media_autopilot_on', !configData.media_autopilot_on)}
                disabled={updatingConfig}
                className={`flex-shrink-0 w-10 h-6 flex items-center rounded-full border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${configData.media_autopilot_on ? 'bg-blue-500 border-blue-500' : 'bg-gray-200 border-gray-300'}`}
                style={{ minWidth: '2.5rem' }}
              >
                <span className="sr-only">Activar/Desactivar piloto automático</span>
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${configData.media_autopilot_on ? 'translate-x-4' : 'translate-x-1'}`}
                />
              </button>
            </div>
            
            {/* Ver Plan de Marketing Button */}
            <div>
              <button
                onClick={() => {
                  setShowConfigPanel(false);
                  window.location.href = '/redes-sociales/plan-marketing-view';
                }}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Rocket className="w-4 h-4" />
                Ver Plan de Marketing
              </button>
            </div>
            
            {/* Desactivar/Activar Agente de Marketing Button */}
            <div>
              <button
                onClick={() => updateConfigSetting('media_agent_on', !configData.media_agent_on)}
                disabled={updatingConfig}
                className={`w-full px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  configData.media_agent_on 
                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {configData.media_agent_on ? 'Desactivar Agente de Marketing' : 'Activar Agente de Marketing'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Universal Confirmation Dialog
  const ConfirmationDialog = () => {
    // Don't render if no dialog is active or no post is being edited
    if (activeDialog === 'none' || !editingPost) return null;
    
    // Determine dialog content based on the active dialog type
    let title, description, confirmHandler;
    
    if (activeDialog === 'generarNueva') {
      title = "Generar nuevo post";
      description = "Crea una imagen y una descripción desde cero. Ideal si querés algo totalmente nuevo.";
      confirmHandler = handleGenerarNuevaImagen;
    }
    
    return (
      <div 
        id={`dialog-${activeDialog}`}
        className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
          <h3 className="text-lg font-medium mb-3">{title}</h3>
          <p className="text-gray-600 mb-4">{description}</p>
          <div className="flex justify-end gap-3 mt-6">
            <button 
              onClick={() => setActiveDialog('none')}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button 
              onClick={confirmHandler}
              className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-8 max-w-content mx-auto">
      {/* Generation modal overlay to ensure the user can read the message even after navigation */}
      <GenerationNotificationModal
        isOpen={showCalendarGenerationModal}
        onClose={() => setShowCalendarGenerationModal(false)}
      />
      {/* Loading overlay for generating a new image (same UX as Crear) */}
      {generatingNewImage && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={GENERATE_DURATION_MS}
        />
      )}
      {/* Single Universal Dialog Component */}
      <ConfirmationDialog />
      {/* Success message notification */}
      {imageEditSuccessMessage && (
        <div className="fixed top-4 right-4 bg-green-100 text-green-800 p-4 rounded-md shadow-lg z-[200] transition-opacity duration-300 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {imageEditSuccessMessage}
        </div>
      )}
      <div className="mb-8">
        {/* Mobile title */}
        <div className="sm:hidden mb-4 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Calendario de contenido</h1>
        </div>
        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between gap-6 mb-6">
          <div className="flex-1 text-left">
            <h1 className="text-lg font-semibold text-gray-900 mt-4">Calendario de contenido</h1>
            <p className="text-gray-600 text-sm">Planifica y visualiza tu contenido para redes sociales</p>
          </div>
          {showInitialLoadingMessage && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="text-blue-700 text-sm">Generando primeras imágenes, este proceso podría demorar unos minutos...</span>
            </div>
          )}
        </div>

        {/* Initial loading message - mobile only (desktop shows it inline in the header) */}
        {showInitialLoadingMessage && (
          <div className="sm:hidden mt-4 p-4 bg-blue-50 border border-blue-100 rounded-md flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <p className="text-blue-700">Generando primeras imágenes, este proceso podría demorar unos minutos...</p>
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/2">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button 
                  onClick={isMobile && weekViewActive ? () => navigateWeek('prev') : handlePrevMonth}
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-gray-300 shadow-sm hover:bg-gray-100 transition-colors"
                  aria-label={isMobile && weekViewActive ? "Semana anterior" : "Mes anterior"}
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                
                <h2 className="text-xl font-medium text-center">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  {isMobile && weekViewActive && (
                    <button 
                      onClick={handleCloseWeekView}
                      className="ml-2 text-sm text-blue-500 hover:text-blue-700"
                    >
                      (Ver mes completo)
                    </button>
                  )}
                </h2>
                
                <button 
                  onClick={isMobile && weekViewActive ? () => navigateWeek('next') : handleNextMonth}
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-gray-300 shadow-sm hover:bg-gray-100 transition-colors"
                  aria-label={isMobile && weekViewActive ? "Semana siguiente" : "Mes siguiente"}
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              <button 
                onClick={() => setShowConfigPanel(true)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Configuración"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
            
            {/* Day Names */}
            <div className="grid grid-cols-7 mb-2 text-center">
              {dayNames.map(day => (
                <div key={day} className="text-sm text-gray-500 font-medium py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className={`grid grid-cols-7 gap-1 transition-all duration-300 ease-in-out ${isMobile && weekViewActive ? 'max-h-24' : ''}`}>
              {displayDays.map((day, index) => {
                // Determine the base styling based on events and date status
                let dayClasses = "relative aspect-square flex flex-col items-center justify-center rounded-full transition-colors";
                
                // Base text color based on month and past dates
                if (!day.isCurrentMonth) {
                  dayClasses += " text-gray-400";
                } else if (day.isPast && !day.isToday) {
                  dayClasses += " text-gray-500";
                } else if (day.isCurrentMonth) {
                  dayClasses += " text-gray-900";
                }
                
                // Special styling for scheduled posts
                if (day.isCurrentMonth && day.events.count > 0) {
                  const now = new Date();
                  const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day.date);
                  const isFutureDay = dayDate > now;
                  
                  // Apply styling to future days AND today (day.isToday)
                  if ((isFutureDay || day.isToday) && day.isCurrentMonth) {
                    if (day.events.confirmed) {
                      dayClasses += " bg-blue-100 font-semibold text-blue-700"; 
                    } else if (day.events.count > 0) {
                      dayClasses += " border-2 border-blue-500 font-semibold text-blue-700";
                    }
                  }
                }
                
                // Today styling - add font-semibold (blue outline or background will be added above if there are events)
                if (day.isToday) {
                  dayClasses += " font-semibold"; 
                }
                
                // Past dates get a gray background - darker if they have posted content
                if (day.isPast && !day.isToday && day.isCurrentMonth) {
                  // Use darker gray for days with posted content
                  if (day.events.posted) {
                    dayClasses += " bg-gray-200"; 
                  } else {
                    dayClasses += " bg-gray-50"; 
                  }
                }
                
                // Selected date styling
                if (selectedDate && day.isCurrentMonth && day.date === selectedDate.getDate() && 
                    currentDate.getMonth() === selectedDate.getMonth()) {
                  // Remove gray text and background for past dates when selected
                  dayClasses = dayClasses.replace(" text-gray-500", "");
                  dayClasses = dayClasses.replace(" bg-gray-50", "");
                  dayClasses = dayClasses.replace(" bg-gray-200", ""); // Also remove darker gray for posted content
                  dayClasses += " bg-blue-500 text-white hover:bg-blue-600";
                } else if (day.isCurrentMonth) {
                  dayClasses += " hover:bg-gray-100";
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(day, index)}
                    className={dayClasses}
                    disabled={!day.isCurrentMonth}
                  >
                    <span className="text-sm md:text-base">{day.date}</span>
                    
                    {/* Blue dot for today */}
                    {day.isToday && (
                      <div className={`w-1.5 h-1.5 rounded-full mt-1 ${
                        selectedDate && day.date === selectedDate.getDate() && 
                        currentDate.getMonth() === selectedDate.getMonth() 
                          ? 'bg-white' 
                          : 'bg-blue-500'
                      }`}></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        <div className="md:w-1/2">
          {/* Mobile-specific UI */}
          {isMobile && (
            <>
              {/* Month view summary - Only shown when week view is not active */}
              {!weekViewActive && (
                <>
                  <div className="bg-white rounded-lg shadow-sm mt-4 p-4 text-center">
                    <p className="text-gray-700 font-medium">
                      {confirmedPosts > 0 
                        ? `Hay ${confirmedPosts} publicaciones programadas para hoy y fechas futuras` 
                        : "No hay publicaciones programadas para hoy o fechas futuras"}
                    </p>
                  
                    {unconfirmedPosts > 0 && (
                    <div className="mt-3">
                      <p className="text-blue-700 font-medium mb-2">Tienes {unconfirmedPosts} publicaciones futuras por revisar</p>
                      <button
                        onClick={showNextUnconfirmedPost}
                        className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
                      >
                        Revisar
                      </button>
                    </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 text-center mt-3">
                    Haz clic en una fecha para ver los detalles de las publicaciones programadas
                  </p>
                </>
              )}
              
              {/* Post Details Card - Only shown in week view when a date with posts is selected */}
              {weekViewActive && selectedDate && selectedDatePosts.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden">
                  {selectedDatePosts.map((post) => {
                    const postDate = new Date(post.scheduled_at);
                    const isPast = postDate < new Date();
                    const isPosted = !!post.posted_at;
                    
                    return (
                      <div key={post.id} className="flex flex-col">
                        {/* Status Header */}
                        <div className={`p-4 ${
                          isPosted ? 'bg-green-500 text-white' : 
                          post.confirmed ? 'bg-blue-500 text-white' : 
                          'bg-blue-100 text-blue-800'
                        }`}>
                          <div className="flex items-center gap-2">
                            {isPosted ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : post.confirmed ? (
                              <Clock className="w-5 h-5" />
                            ) : (
                              <Clock className="w-5 h-5" />
                            )}
                            <p className="font-medium">
                              {isPosted 
                                ? `Publicado el ${formatDate(post.posted_at!)} a las ${formatTime(post.posted_at!)}`
                                : isPast
                                  ? "Esta imagen fue generada pero nunca fue subida ni confirmada"
                                  : post.confirmed 
                                    ? `Programado para subirse el ${formatDate(post.scheduled_at)} a las ${formatTime(post.scheduled_at)}`
                                    : `Si se confirma, se subirá el ${formatDate(post.scheduled_at)} a las ${formatTime(post.scheduled_at)}`
                              }
                            </p>
                            {!editingPost && !editingImageChat && (!isPast || (isPast && !isPosted)) && (
                              <button 
                                className={`ml-auto p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors ${post.confirmed ? 'text-white' : 'text-blue-800'}`}
                                onClick={() => setEditingPost(post)}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Post Content or Edit Form */}
                        <div className="p-4">
                          {editingPost && editingPost.id === post.id ? (
                            editingImageChat ? (
                              <>
                                {post.post_description ? (
                                  <p className="text-gray-700 mb-4">{post.post_description}</p>
                                ) : (
                                  <p className="text-gray-500 italic mb-4">Sin descripción</p>
                                )}

                                {/* Imagen con overlay de estado */}
                                <div className="relative rounded-md overflow-hidden mb-4 aspect-square shadow-sm max-h-80">
                                  {(() => {
                                    const currentUrl = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].url : (post.imageUrl || '');
                                    return (
                                      <>
                                        {(isSubmitting || showEditSuccess) && (
                                          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                                              {isSubmitting ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                                                  <span className="text-xs font-medium text-gray-700">Editando imagen</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Check className="w-4 h-4 text-green-600" />
                                                  <span className="text-xs font-medium text-gray-700">Imagen editada</span>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {imageLoading || loadingImages[post.id] ? (
                                          <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                          </div>
                                        ) : currentUrl ? (
                                          <img
                                            src={currentUrl}
                                            alt="Imagen generada para el post"
                                            className="w-full h-full object-contain"
                                            onError={e => {
                                              e.currentTarget.onerror = null;
                                              e.currentTarget.src = '';
                                            }}
                                          />
                                        ) : (
                                          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                                            <ImageIcon className="w-12 h-12 text-gray-400" />
                                            <span className="text-gray-500 mt-2">Sin imagen generada</span>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>

                                {/* Controles inferiores (desktop) */}
                                <div className="hidden md:flex flex-col gap-3 border-t border-gray-200 pt-3">
                                  <div className="relative flex-1">
                                    <input
                                      type="text"
                                      value={instruction}
                                      onChange={(e) => setInstruction(e.target.value)}
                                      placeholder="Describí el cambio que querés hacer"
                                      disabled={isSubmitting}
                                      className="w-full pl-10 pr-12 h-11 rounded-full bg-gray-50 sm:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 border border-gray-300 text-sm placeholder:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    />
                                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                      <Sparkles className="w-4 h-4" aria-hidden="true" />
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleSendInstruction}
                                      disabled={isSubmitting || !instruction.trim()}
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Enviar instrucción"
                                    >
                                      <ArrowRight className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-2 self-end">
                                    <button
                                      type="button"
                                      onClick={handleUndo}
                                      disabled={currentIndex <= 0}
                                      className="h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Deshacer"
                                    >
                                      <Undo2 className="w-4 h-4" />
                                      Deshacer
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleRedo}
                                      disabled={currentIndex < 0 || currentIndex >= history.length - 1}
                                      className="h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Rehacer"
                                    >
                                      <Redo2 className="w-4 h-4" />
                                      Rehacer
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveAndExit}
                                      disabled={isSaving || !hasEdits}
                                      className="h-10 px-4 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <Save className="w-4 h-4" />
                                      Guardar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingImageChat(false); setEditingPost(null); setInstruction(''); setHistory([]); setCurrentIndex(-1); }}
                                      className="h-10 px-4 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>

                                {/* Controles (mobile) */}
                                <div className="md:hidden bg-white pt-3 flex-shrink-0 space-y-3">
                                  <div className="relative">
                                    <input
                                      type="text"
                                      value={instruction}
                                      onChange={(e) => setInstruction(e.target.value)}
                                      placeholder="Describí el cambio que querés hacer..."
                                      disabled={isSubmitting}
                                      className="w-full pl-10 pr-12 h-11 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 border border-gray-300 text-sm placeholder:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    />
                                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                      <Sparkles className="w-4 h-4" aria-hidden="true" />
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleSendInstruction}
                                      disabled={isSubmitting || !instruction.trim()}
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Enviar instrucción"
                                    >
                                      <ArrowRight className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={handleUndo}
                                        disabled={currentIndex <= 0}
                                        className="h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        aria-label="Deshacer"
                                      >
                                        <Undo2 className="w-4 h-4" />
                                        <span className="text-sm">Deshacer</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleRedo}
                                        disabled={currentIndex < 0 || currentIndex >= history.length - 1}
                                        className="h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        aria-label="Rehacer"
                                      >
                                        <Redo2 className="w-4 h-4" />
                                        <span className="text-sm">Rehacer</span>
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={handleSaveAndExit}
                                      disabled={isSaving || !hasEdits}
                                      className="flex-1 h-10 px-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <Save className="w-4 h-4" />
                                      <span className="text-sm">Guardar</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingImageChat(false); setEditingPost(null); setInstruction(''); setHistory([]); setCurrentIndex(-1); }}
                                      className="flex-1 h-10 px-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <span className="text-sm">Cancelar</span>
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : editingDescription ? (
                              // Description editing UI
                              <>
                                {/* Editable description field */}
                                <div className="mb-4">
                                  <textarea
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[100px]"
                                    placeholder="Descripción del post"
                                  />
                                </div>

                                {/* Post Image */}
                                <div className="rounded-md overflow-hidden mb-4 aspect-square shadow-sm max-h-80">
                                  {imageLoading || loadingImages[post.id] ? (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                    </div>
                                  ) : post.imageUrl ? (
                                    <img 
                                      src={post.imageUrl} 
                                      alt="Imagen generada para el post"
                                      className="w-full h-full object-contain"
                                      onError={e => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = '';
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                                      <ImageIcon className="w-12 h-12 text-gray-400" />
                                      <span className="text-gray-500 mt-2">Sin imagen generada</span>
                                    </div>
                                  )}
                                </div>

                                {/* Image action buttons */}
                                <div className="flex flex-col space-y-2 mb-4">
                                  <button
                                    onClick={() => setActiveDialog('generarNueva')}
                                    className="w-full px-3 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors text-sm flex items-center justify-center gap-2 font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <ImageIcon className="w-4 h-4" /> Generar nueva imagen
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingImageChat(true);
                                      setImageEditSuccessMessage(''); // Clear any existing success message
                                    }}
                                    className="w-full px-3 py-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors text-sm flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <Edit className="w-4 h-4" /> Modificar
                                  </button>
                                </div>

                                {/* Single toggle button */}
                                <div className="flex justify-end pt-2 border-t">
  <button
    onClick={() => {
      if (imageHasBeenEdited) {
        handleSaveEditedPost();
      } else {
        setEditingPost(null);
        setEditingDescription(false);
      }
    }}
    disabled={updatingPost}
    className={
      `px-4 py-2 rounded-full transition-colors disabled:opacity-50 shadow-sm focus:ring-2 focus:ring-offset-2 font-normal w-full md:w-auto ` +
      (updatingPost
        ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500'
        : imageHasBeenEdited
        ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500'
        : 'bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500')
    }
  >
    {updatingPost ? 'Guardando...' : imageHasBeenEdited ? 'Listo' : 'Cancelar'}
  </button>
</div>                             </>
                            ) : (
                              // Time editing UI
                              <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fecha
                                  </label>
                                  <input
                                    type="date"
                                    value={editedScheduledDate}
                                    onChange={(e) => setEditedScheduledDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hora
                                  </label>
                                  <input
                                    type="time"
                                    value={editedScheduledTime}
                                    onChange={(e) => setEditedScheduledTime(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  />
                                </div>
                                <div className="flex justify-end pt-2">
  <div className="flex justify-end gap-3 w-full">
    <button
      onClick={() => {
        setEditingPost(null);
      }}
      disabled={updatingPost}
      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 font-normal"
    >
      Cancelar
    </button>
    <button
      onClick={() => {
        handleSaveEditedPost();
      }}
      disabled={updatingPost}
      className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
    >
      {updatingPost ? 'Guardando...' : 'Confirmar'}
    </button>
  </div>
</div>
                              </div>
                            )
                          ) : (
                            <>
                              {!configData.media_agent_on ? (
                                <p className="text-red-600 mb-4">
                                  El Agente de Marketing fue desactivado. Vuelve a activarlo para que la generación y posteo de contenido se reactive
                                </p>
                              ) : (
                                <>
                                  {post.post_description ? (
                                    <p className="text-gray-700 mb-4">{post.post_description}</p>
                                  ) : (
                                    <p className="text-gray-500 italic mb-4">Sin descripción</p>
                                  )}
                                  
                                  {/* Post Image */}
                                  <div className="rounded-md overflow-hidden mb-4 aspect-square shadow-sm max-h-80 md:max-h-[480px] md:h-[480px] md:aspect-[1/1] md:w-full">
                                    {imageLoading || loadingImages[post.id] ? (
                                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                      </div>
                                    ) : post.imageUrl ? (
                                      <img 
                                        src={post.imageUrl} 
                                        alt="Imagen generada para el post"
                                        className="w-full h-full object-contain"
                                        onError={e => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = '';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                                        <ImageIcon className="w-12 h-12 text-gray-400" />
                                        <span className="text-gray-500 mt-2">Sin imagen generada</span>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                              
                              {/* Action Buttons - Only show for future posts */}
                              {(!isPast || (isPast && !isPosted)) && (
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => {
                                      setEditingPost(post);
                                      setEditingDescription(true);
                                    }}
                                    className="px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-full transition-colors font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    Editar
                                  </button>
                                  
                                  {!isPosted && !isPast && configData.media_agent_on && (
                                    <button 
                                      onClick={() => handleConfirmPost(post.id, !post.confirmed)}
                                      disabled={updatingPost}
                                      className={`px-4 py-2 rounded-full transition-colors font-normal shadow-sm ${
                                        post.confirmed 
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-2 focus:ring-offset-2 focus:ring-red-500' 
                                          : 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                      }`}
                                    >
                                      {updatingPost ? 'Actualizando...' : post.confirmed ? 'Cancelar' : 'Confirmar'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* No posts message - Only shown in week view when a date is selected but has no posts */}
              {weekViewActive && selectedDate && selectedDatePosts.length === 0 && (
                <div className="bg-white rounded-lg shadow-sm mt-4 p-6 text-center">
                  <p className="text-gray-500">No hay publicaciones programadas para esta fecha</p>
                </div>
              )}
            </>
          )}
          
          {/* Desktop-specific UI */}
          {!isMobile && (
            <>
              {/* Show post details when a date is selected, otherwise show summary */}
              {selectedDate && selectedDatePosts.length > 0 ? (
                <div className="bg-white rounded-md shadow-sm max-h-[900px] overflow-y-auto flex flex-col gap-4 p-2">
                  {selectedDatePosts.map((post) => {
                    const postDate = new Date(post.scheduled_at);
                    const isPast = postDate < new Date();
                    const isPosted = !!post.posted_at;
                    return (
                      <div key={post.id} className="flex flex-col">
                        {/* Status Header */}
                        <div className={`p-4 ${
                          isPosted ? 'bg-green-500 text-white' : 
                          post.confirmed ? 'bg-blue-500 text-white' : 
                          'bg-blue-100 text-blue-800'
                        }`}>
                          <div className="flex items-center gap-2">
                            {isPosted ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : post.confirmed ? (
                              <Clock className="w-5 h-5" />
                            ) : (
                              <Clock className="w-5 h-5" />
                            )}
  <p className="font-medium">
                              {isPosted 
                                ? `Publicado el ${formatDate(post.posted_at!)} a las ${formatTime(post.posted_at!)}`
                                : isPast
                                  ? "Esta imagen fue generada pero nunca fue subida ni confirmada"
                                  : post.confirmed 
                                    ? `Programado para subirse el ${formatDate(post.scheduled_at)} a las ${formatTime(post.scheduled_at)}`
                                    : `Si se confirma, se subirá el ${formatDate(post.scheduled_at)} a las ${formatTime(post.scheduled_at)}`
                              }
                            </p>
                            {!editingPost && !editingImageChat && (!isPast || (isPast && !isPosted)) && (
                              <button 
                                className={`ml-auto p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors ${post.confirmed ? 'text-white' : 'text-blue-800'}`}
                                onClick={() => setEditingPost(post)}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Post Content or Edit Form */}
                        <div className="p-4 flex-1 overflow-y-auto">
                          {editingPost && editingPost.id === post.id ? (
                            editingImageChat ? (
                              <>
                                {post.post_description ? (
                                  <p className="text-gray-700 mb-4">{post.post_description}</p>
                                ) : (
                                  <p className="text-gray-500 italic mb-4">Sin descripción</p>
                                )}
                                {/* Imagen con overlay de estado (desktop) */}
                                <div className="relative rounded-md overflow-hidden mb-4 aspect-square shadow-sm max-h-80 md:max-h-[480px] md:h-[480px] md:aspect-[1/1] md:w-full">
                                  {(() => {
                                    const currentUrl = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].url : (post.imageUrl || '');
                                    return (
                                      <>
                                        {(isSubmitting || showEditSuccess) && (
                                          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                                              {isSubmitting ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                                                  <span className="text-xs font-medium text-gray-700">Editando imagen</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Check className="w-4 h-4 text-green-600" />
                                                  <span className="text-xs font-medium text-gray-700">Imagen editada</span>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {imageLoading || loadingImages[post.id] ? (
                                          <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                          </div>
                                        ) : currentUrl ? (
                                          <img
                                            src={currentUrl}
                                            alt="Imagen generada para el post"
                                            className="w-full h-full object-contain"
                                            onError={e => {
                                              e.currentTarget.onerror = null;
                                              e.currentTarget.src = '';
                                            }}
                                          />
                                        ) : (
                                          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                                            <ImageIcon className="w-12 h-12 text-gray-400" />
                                            <span className="text-gray-500 mt-2">Sin imagen generada</span>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                {/* Controles (solo desktop) */}
                                <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
                                  <div className="relative flex-1">
                                    <input
                                      type="text"
                                      value={instruction}
                                      onChange={(e) => setInstruction(e.target.value)}
                                      placeholder="Describí el cambio que querés hacer"
                                      disabled={isSubmitting}
                                      className="w-full pl-10 pr-12 h-11 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 border border-gray-300 text-sm placeholder:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    />
                                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                      <Sparkles className="w-4 h-4" aria-hidden="true" />
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleSendInstruction}
                                      disabled={isSubmitting || !instruction.trim()}
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Enviar instrucción"
                                    >
                                      <ArrowRight className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={handleUndo}
                                      disabled={currentIndex <= 0}
                                      className="h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Deshacer"
                                    >
                                      <Undo2 className="w-4 h-4" />
                                      Deshacer
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleRedo}
                                      disabled={currentIndex < 0 || currentIndex >= history.length - 1}
                                      className="h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      aria-label="Rehacer"
                                    >
                                      <Redo2 className="w-4 h-4" />
                                      Rehacer
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveAndExit}
                                      disabled={isSaving || !hasEdits}
                                      className="h-10 px-4 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      <Save className="w-4 h-4" />
                                      Guardar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingImageChat(false); setEditingPost(null); setInstruction(''); setHistory([]); setCurrentIndex(-1); }}
                                      className="h-10 px-4 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : editingDescription ? (
                              // Description editing UI for desktop view 
                              <>
                                {/* Editable description field */}
                                <div className="mb-4">
                                  <textarea
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[150px] mb-4"
                                    placeholder="Descripción del post"
                                  />
                                </div>

                                {/* Post Image */}
                                <div className="rounded-md overflow-hidden mb-4 aspect-square shadow-sm max-h-80 md:max-h-[480px] md:h-[480px] md:aspect-[1/1] md:w-full">
                                  {imageLoading || loadingImages[post.id] ? (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                    </div>
                                  ) : post.imageUrl ? (
                                    <img 
                                      src={post.imageUrl} 
                                      alt="Imagen generada para el post"
                                      className="w-full h-full object-contain"
                                      onError={e => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = '';
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                                      <ImageIcon className="w-12 h-12 text-gray-400" />
                                      <span className="text-gray-500 mt-2">Sin imagen generada</span>
                                    </div>
                                  )}
                                </div>

                                {/* Image action buttons */}
                                <div className="grid grid-cols-4 gap-3 mb-4">
                                  <button
                                    onClick={() => setActiveDialog('generarNueva')}
                                    className="col-span-2 flex px-3 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors text-sm items-center justify-center gap-2 font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <ImageIcon className="w-4 h-4" /> Generar nueva imagen
                                  </button>
                                  <button
                                    onClick={() => setEditingImageChat(true)}
                                    className="col-span-2 flex px-3 py-3 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors text-sm items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <Edit className="w-4 h-4" /> Modificar
                                  </button>
                                </div>

                                {/* Single toggle button */}
                                <div className="flex justify-center md:justify-end pt-2 border-t">
                                  <button
                                    onClick={() => {
                                      if (imageHasBeenEdited) {
                                        // If image was changed, save changes
                                        handleSaveEditedPost();
                                      } else {
                                        // If no image change, just cancel editing
                                        setEditingPost(null);
                                        setEditingDescription(false);
                                      }
                                    }}
                                    disabled={updatingPost}
                                    className={
                                      `px-4 py-2 rounded-full transition-colors disabled:opacity-50 shadow-sm focus:ring-2 focus:ring-offset-2 font-normal ` +
                                      (updatingPost
                                        ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500'
                                        : imageHasBeenEdited
                                        ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500'
                                        : 'bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500')
                                    }
                                  >
                                    {updatingPost ? 'Guardando...' : imageHasBeenEdited ? 'Listo' : 'Cancelar'}
                                  </button>
                                </div>
                              </>
                            ) : (
                              // Time editing UI
                              <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fecha
                                  </label>
                                  <input
                                    type="date"
                                    value={editedScheduledDate}
                                    onChange={(e) => setEditedScheduledDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hora
                                  </label>
                                  <input
                                    type="time"
                                    value={editedScheduledTime}
                                    onChange={(e) => setEditedScheduledTime(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  />
                                </div>
                                <div className="flex justify-end gap-3 pt-2">
                                  <button
                                    onClick={() => {
                                      setEditingPost(null);
                                    }}
                                    disabled={updatingPost}
                                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 font-normal"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleSaveEditedPost();
                                    }}
                                    disabled={updatingPost}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
                                  >
                                    {updatingPost ? 'Guardando...' : 'Confirmar'}
                                  </button>
                                </div>
                              </div>
                            )
                          ) : (
                            <>
                              {!configData.media_agent_on ? (
                                <p className="text-red-600 mb-4">
                                  El Agente de Marketing fue desactivado. Vuelve a activarlo para que la generación y posteo de contenido se reactive
                                </p>
                              ) : (
                                <>
                                  {post.post_description ? (
                                    <p className="text-gray-700 mb-4">{post.post_description}</p>
                                  ) : (
                                    <p className="text-gray-500 italic mb-4">Sin descripción</p>
                                  )}
                                  
                                  {/* Post Image */}
                                  <div className="rounded-md overflow-hidden mb-4 aspect-square shadow-sm max-h-80 md:max-h-[480px] md:h-[480px] md:aspect-[1/1] md:w-full">
                                    {(() => { console.log('Calendar Post Object:', post); return null; })()}
                                    {imageLoading || loadingImages[post.id] ? (
                                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                      </div>
                                    ) : post.imageUrl ? (
                                      <img 
                                        src={post.imageUrl} 
                                        alt="Imagen generada para el post"
                                        className="w-full h-full object-contain"
                                        onError={e => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = '';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                                        <ImageIcon className="w-12 h-12 text-gray-400" />
                                        <span className="text-gray-500 mt-2">Sin imagen generada</span>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Action Buttons - Show if not editing and (future OR past unposted) */}
                        {(!editingPost || editingPost.id !== post.id) && (!isPast || (isPast && !isPosted)) ? (
                          <div className="p-4 border-t flex justify-between">
                            <button
                              onClick={() => {
                                setEditingPost(post);
                                setEditingDescription(true);
                              }}
                              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors shadow-sm font-normal focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Editar
                            </button>
                            
                            {!isPosted && !isPast && configData.media_agent_on && (
                              <button 
                                onClick={() => handleConfirmPost(post.id, !post.confirmed)}
                                disabled={updatingPost}
                                className={`px-4 py-2 rounded-full transition-colors font-normal shadow-sm ${
                                  post.confirmed 
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-2 focus:ring-offset-2 focus:ring-red-500' 
                                    : 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                }`}
                              >
                                {updatingPost ? 'Actualizando...' : post.confirmed ? 'Cancelar' : 'Confirmar'}
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : selectedDate && selectedDatePosts.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-6 text-center h-full flex items-center justify-center">
                  <p className="text-gray-500">No hay publicaciones programadas para esta fecha</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm p-6 h-full">
                  <div className="flex flex-col h-full justify-center">
                    <p className="text-gray-700 font-medium text-center">
                      {confirmedPosts > 0 
                        ? `Hay ${confirmedPosts} publicaciones programadas` 
                        : "No hay publicaciones programadas"}
                    </p>
                    
                    
                    {unconfirmedPosts > 0 && (
                      <div className="mt-6 text-center">
                        <p className="text-blue-700 font-medium mb-3">Tienes {unconfirmedPosts} publicaciones para revisar</p>
                        <button
                          onClick={showNextUnconfirmedPost}
                          className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors mx-auto shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
                        >
                          Revisar
                        </button>
                      </div>
                    )}
                    
                    <p className="text-sm text-gray-500 text-center mt-6">
                      Haz clic en una fecha para ver los detalles de las publicaciones programadas
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Configuration Panel */}
      {showConfigPanel && <ConfigurationPanel />}

      {/* The universal dialog component is already rendered at the top level */}
    </div>
  );
};

export default Calendar;
